import { createAction, createSlice } from '@reduxjs/toolkit';
import { runDocumentNumberTypeMutation, runDocumentNumberTypeQuery } from '@/pages/document-number-type/document-number-type.query';
import { notifyUnlessUnauthorized } from '@/pages/shared/rtk-query-helpers';
import { changeTableRow, hidePanel, setFilter } from '@/pages/shared/shared.reducer';
import panels from '@/constants/panels';
import { openSuccessNotification } from '@/notification';

const CHANGE_NUMBER_TYPE_PANEL_TITLE = 'CHANGE_NUMBER_TYPE_PANEL_TITLE';
const NUMBER_TYPE_VALUE_SET = "NUMBER_TYPE_VALUE_SET";
const NUMBER_TYPE_LOAD_SUCCESS = 'NUMBER_TYPE_LOAD_SUCCESS';
const CHANGE_NUMBER_TYPE_PANEL = 'CHANGE_NUMBER_TYPE_PANEL';
const LOADING_NUMBER_TYPE_PANEL = 'LOADING_NUMBER_TYPE_PANEL';
const SET_FIELDS_NUMBER_TYPE = 'SET_FIELDS_NUMBER_TYPE';
const ADD_FIELD_TO_ARRAY = 'ADD_FIELD_TO_ARRAY';
const DELETE_FIELD_FROM_ARRAY = 'DELETE_FIELD_FROM_ARRAY';
const CHANGE_NUMBER_TYPE_FIELD_ORDER_ARRAY = 'CHANGE_NUMBER_TYPE_FIELD_ORDER_ARRAY';

const initialState = {
    loading: false,
    numberTypePanelTitle: '',
    item: {},
    editItem: {},
    fields: [],
    accounts: [],
    sites: []
}

const bySortOrder = (a, b) => (a.sortOrder > b.sortOrder) ? 1 : ((b.sortOrder > a.sortOrder) ? -1 : 0);

// Legacy action type strings are preserved so existing dispatch sites keep working.
export const setNumberTypePanelTitle = createAction(CHANGE_NUMBER_TYPE_PANEL_TITLE, (value) => ({
    payload: { value }
}));

export const setValue = createAction(NUMBER_TYPE_VALUE_SET, (key, value) => ({
    payload: { key, value }
}));

export const setNumberTypeItem = createAction(NUMBER_TYPE_LOAD_SUCCESS, (item) => ({
    payload: item
}));

export const changeNumberType = createAction(CHANGE_NUMBER_TYPE_PANEL, (key, value) => ({
    payload: { key, value }
}));

export const setLoadingPanelNumberType = createAction(LOADING_NUMBER_TYPE_PANEL, (value) => ({
    payload: { value }
}));

export const setFields = createAction(SET_FIELDS_NUMBER_TYPE, (value) => ({
    payload: { value }
}));

export const addFieldToArray = createAction(ADD_FIELD_TO_ARRAY, (value) => ({
    payload: { value }
}));

export const deleteFieldFieldArray = createAction(DELETE_FIELD_FROM_ARRAY, (value) => ({
    payload: { value }
}));

export const changeArray = createAction(CHANGE_NUMBER_TYPE_FIELD_ORDER_ARRAY, (numberTypeFieldOrderId, sortOrder, saveChanges) => ({
    payload: { numberTypeFieldOrderId, sortOrder, saveChanges }
}));

const numberTypeSlice = createSlice({
    name: 'numberType',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(setNumberTypePanelTitle, (draft, action) => {
                draft.numberTypePanelTitle = action.payload.value;
            })
            .addCase(setValue, (draft, action) => {
                draft[action.payload.key] = action.payload.value;
            })
            .addCase(setNumberTypeItem, (draft, action) => {
                draft.loading = false;
                const numberTypeFieldOrder = [...(action.payload.numberTypeFieldOrder || [])].sort(bySortOrder);
                draft.item = { ...action.payload, numberTypeFieldOrder: numberTypeFieldOrder.map(field => ({ ...field })) };
                draft.editItem = { ...action.payload, numberTypeFieldOrder: numberTypeFieldOrder.map(field => ({ ...field })) };
            })
            .addCase(setFields, (draft, action) => {
                draft.fields = action.payload.value;
            })
            .addCase(changeNumberType, (draft, action) => {
                draft.editItem[action.payload.key] = action.payload.value;
            })
            .addCase(setLoadingPanelNumberType, (draft, action) => {
                draft.loading = action.payload.value;
            })
            .addCase(addFieldToArray, (draft, action) => {
                draft.item.numberTypeFieldOrder = [...draft.item.numberTypeFieldOrder, action.payload.value].sort(bySortOrder);
                draft.editItem.numberTypeFieldOrder = [...draft.editItem.numberTypeFieldOrder, action.payload.value].sort(bySortOrder);
            })
            .addCase(deleteFieldFieldArray, (draft, action) => {
                draft.item.numberTypeFieldOrder = draft.item.numberTypeFieldOrder.filter(item => item.numberTypeFieldOrderId != action.payload.value);
                draft.editItem.numberTypeFieldOrder = draft.editItem.numberTypeFieldOrder.filter(item => item.numberTypeFieldOrderId != action.payload.value);
            })
            .addCase(changeArray, (draft, action) => {
                const tmp = draft.editItem.numberTypeFieldOrder.map(element => ({ ...element }));
                const index = tmp.findIndex(item => item.numberTypeFieldOrderId == action.payload.numberTypeFieldOrderId);
                tmp[index].sortOrder = action.payload.sortOrder;

                draft.editItem.numberTypeFieldOrder = [...tmp];

                if (action.payload.saveChanges) {
                    const sorted = [...tmp].sort(bySortOrder);
                    draft.item.numberTypeFieldOrder = sorted;
                    draft.editItem.numberTypeFieldOrder = sorted;
                }
            });
    }
});

const numberTypeReducer = numberTypeSlice.reducer;

export const createNumberType = (model, needClose) => {
    return (dispatch) => {
        runDocumentNumberTypeMutation(dispatch, 'createNumberType', model)
            .then(data => {
                dispatch(changeTableRow('documentNumberTypeTable', { ...data, numberTypeName: data.name }, 'numberTypeId'))
                openSuccessNotification('Number Type was created');
                dispatch(setNumberTypeItem(data))
                needClose && dispatch(hidePanel(panels.numberType))
            })
            .catch(error => {
                notifyUnlessUnauthorized(error, `Error. Can't create Number Type`);
            })
    }
}

export const changeSortOrderForNumberTypeFieldOrderValue = (numberTypeFieldOrderId, sortOrder) => {
    return (dispatch) => {
        dispatch(changeArray(numberTypeFieldOrderId, sortOrder));
    }
}

export const changeSortOrderForNumberTypeFieldOrder = (numberTypeFieldOrderId, sortOrder) => {
    return (dispatch) => {
        runDocumentNumberTypeMutation(dispatch, 'changeSortOrderForNumberTypeFieldOrder', { numberTypeFieldOrderId, sortOrder })
            .then(data => {
                openSuccessNotification(`Sort Order was changed`);
                dispatch(changeArray(data.numberTypeFieldOrderId, data.sortOrder, true));
            })
            .catch(error => {
                notifyUnlessUnauthorized(error, `Error. Can't update sort order`);
            })
    }
}

export const deleteNumberType = (id, notNeedDeleteAndShowMess) => {
    return (dispatch) => {
        runDocumentNumberTypeMutation(dispatch, 'deleteNumberType', id)
            .then(data => {
                !notNeedDeleteAndShowMess && dispatch(setFilter('documentNumberTypeTable', 'key', Math.floor(Math.random() * Math.floor(2500))))
                !notNeedDeleteAndShowMess && dispatch(setNumberTypeItem(data));
                !notNeedDeleteAndShowMess && openSuccessNotification('Number Type was deleted');
            })
            .catch(error => {
                notifyUnlessUnauthorized(error, `Error. Can't delete Number Type`);
            })
    }
}

export const restoreNumberType = id => {
    return (dispatch) => {
        runDocumentNumberTypeMutation(dispatch, 'restoreNumberType', id)
            .then(data => {
                dispatch(setFilter('documentNumberTypeTable', 'key', Math.floor(Math.random() * Math.floor(2500))));
                dispatch(setNumberTypeItem(data));
                openSuccessNotification('Number Type was restored');
            })
            .catch(error => {
                notifyUnlessUnauthorized(error, `Error. Can't restore Number Type`);
            })
    }
}

export const getFieldsBySiteId = (siteId) => {
    return (dispatch) => {
        runDocumentNumberTypeQuery(dispatch, 'getFieldsBySiteId', siteId)
            .then(data => {
                dispatch(setFields(data));
            })
            .catch(error => {
                notifyUnlessUnauthorized(error, `Error. Can't receive list of Fields`);
            })
    }
}

export const addNumberTypeFieldOrders = (numberTypeId, numberTypeFieldId, sortOrder) => {
    return (dispatch) => {
        runDocumentNumberTypeMutation(dispatch, 'addNumberTypeFieldOrders', { numberTypeId, numberTypeFieldId, sortOrder })
            .then(data => {
                dispatch(addFieldToArray(data));
                openSuccessNotification(`Document Number Component added`)
            })
            .catch(error => {
                notifyUnlessUnauthorized(error, `Error. Can't create Document Number Component`);
            })
    }
}

export const isNumberTypeNameValid = (dispatch, numberTypeName, numberTypeId, siteId) => {
    return runDocumentNumberTypeQuery(dispatch, 'numberTypeNameValid', { numberTypeName, numberTypeId, siteId })
        .catch(error => {
            notifyUnlessUnauthorized(error, `Error. Please try later`);
        })
}

export const updateNumberType = (model, needClose) => {
    return (dispatch) => {
        runDocumentNumberTypeMutation(dispatch, 'updateNumberType', model)
            .then(data => {
                dispatch(changeTableRow('documentNumberTypeTable', { ...data, numberTypeName: data.name }, 'numberTypeId'))
                dispatch(setNumberTypeItem(data));
                openSuccessNotification('Number Type was updated');

                needClose && dispatch(hidePanel(panels.numberType))
            })
            .catch(error => {
                notifyUnlessUnauthorized(error, `Error. Can't update Number Type`);
            })
    }
}

export const getAccountsNumberTypes = () => {
    return (dispatch) => {
        runDocumentNumberTypeQuery(dispatch, 'accountsNumberType')
            .then(data => {
                dispatch(setValue('accounts', data))
            })
            .catch(error => {
                notifyUnlessUnauthorized(error, `Error. Can't load accounts`);
            })
    }
}

export const getSitesNumberTypes = () => {
    return (dispatch) => {
        runDocumentNumberTypeQuery(dispatch, 'sitesNumberType')
            .then(data => {
                dispatch(setValue('sites', data))
            })
            .catch(error => {
                notifyUnlessUnauthorized(error, `Error. Can't load sites`);
            })
    }
}

export const getNumberType = (id) => {
    return (dispatch) => {
        dispatch(setLoadingPanelNumberType(true));

        runDocumentNumberTypeQuery(dispatch, 'getNumberType', id)
            .then(item => {
                dispatch(setNumberTypeItem(item))
            })
            .catch(error => {
                notifyUnlessUnauthorized(error, `Error. Can't load Document Number Setup`);
            })
            .finally(() => {
                setTimeout(() => {
                    dispatch(setLoadingPanelNumberType(false));
                }, 0)
            })
    }
}

export const deleteFieldSortOrder = id => {
    return (dispatch) => {
        runDocumentNumberTypeMutation(dispatch, 'deleteFieldSortOrder', id)
            .then(() => {
                dispatch(deleteFieldFieldArray(id));
                openSuccessNotification(`Document Number Component was deleted`);
            })
            .catch(error => {
                notifyUnlessUnauthorized(error, `Error. Can't delete Document Number Component`);
            })
    }
}

export default numberTypeReducer;
